import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatApiError, getBackendOrigin } from '@/lib/api';
import { buildApartmentCreatePayload } from '@/lib/apartmentPayload';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, Users, Home, Calendar, FileText, Eye, Plus, Link2, Trash2, CheckCircle, Download, ExternalLink, X, Mail, Pencil, Check } from 'lucide-react';
import EmailApprovalDialog from '@/components/emails/EmailApprovalDialog';
import { useEnums } from '@/lib/enums';
import AdminNavTabs from '@/components/admin/AdminNavTabs';
import { resolveAdminActiveTab, getAdminTabPath } from '@/lib/adminNav';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-blue-50 text-blue-700',
  approved: 'bg-violet-50 text-violet-700',
  in_search: 'bg-amber-50 text-amber-700',
  matched: 'bg-emerald-50 text-emerald-700',
  archived: 'bg-slate-100 text-slate-600',
  rejected: 'bg-red-50 text-red-700',
  requested: 'bg-blue-50 text-blue-700', confirmed: 'bg-emerald-50 text-emerald-700',
  cancelled: 'bg-gray-100 text-gray-500', available: 'bg-blue-50 text-blue-700',
};
// Legacy hardcoded list - kept as a typed fallback while the server enums
// load. Once useEnums() resolves, the component uses the server's list
// instead (see `appStatuses` inside AdminPanel).
const APP_STATUSES_FALLBACK = ['draft','submitted','approved','in_search','matched','archived','rejected'];
const STATUS_LABELS = {
  draft: 'draft',
  submitted: 'submitted',
  approved: 'approved',
  in_search: 'in search',
  matched: 'matched',
  archived: 'archived',
  rejected: 'rejected',
};
// Stage transitions are fully reversible. Any application can move to any
// other status (admins may reinstate archived/rejected, downgrade approved
// after new info, etc.). SUGGESTED_NEXT is kept only as a UX hint for the
// natural happy-path - not as an enforcement rule.
const SUGGESTED_NEXT = {
  draft:     ['submitted'],
  submitted: ['approved', 'rejected'],
  approved:  ['in_search', 'rejected'],
  in_search: ['matched', 'rejected'],
  matched:   ['archived'],
  rejected:  ['approved'],
  archived:  ['approved'],
};
const formatStatus = (s) => STATUS_LABELS[s] || (s || '').replace('_', ' ');

// Compact one-liner shown under an applicant's name in the table, so the admin
// can remember the dossier at a glance without opening it.
function buildApplicantSummary(app) {
  if (!app) return '';
  const criteria = app.search_criteria || {};
  const parts = [];
  if (criteria.property_type) parts.push(String(criteria.property_type));
  if (criteria.num_rooms) parts.push(`${criteria.num_rooms} rooms`);
  const areas = Array.isArray(criteria.neighborhoods) ? criteria.neighborhoods.filter(Boolean) : [];
  if (areas.length) {
    const head = areas.slice(0, 2).join(', ');
    parts.push(areas.length > 2 ? `${head} +${areas.length - 2}` : head);
  }
  return parts.join(' \u00b7 ');
}

function latestOpenTodo(app) {
  const todos = Array.isArray(app?.admin_todos) ? app.admin_todos : [];
  const open = todos.filter((t) => t && t.status !== 'done');
  if (open.length === 0) return null;
  open.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  return open[0];
}
// Button styling for the dossier decision row - one entry per target status.
const DECISION_BUTTON_META = {
  submitted: { label: 'Mark submitted', variant: 'outline', className: 'h-8 text-xs border-blue-400 text-blue-700' },
  approved:  { label: 'Approve',        variant: 'default', className: 'h-8 text-xs bg-violet-600 hover:bg-violet-700 text-white' },
  in_search: { label: 'Start search',   variant: 'default', className: 'h-8 text-xs bg-amber-500 hover:bg-amber-600 text-white' },
  matched:   { label: 'Mark matched',   variant: 'default', className: 'h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white' },
  archived:  { label: 'Archive',        variant: 'outline', className: 'h-8 text-xs border-slate-400 text-slate-700' },
  rejected:  { label: 'Reject',         variant: 'destructive', className: 'h-8 text-xs' },
};
const VISIT_STATUSES = ['requested','confirmed','completed','cancelled'];
const SCORE_CATEGORY_CONFIG = [
  { key: 'financial_strength', label: 'Income', max: 35 },
  { key: 'payment_reliability', label: 'Reliability', max: 20 },
  { key: 'profile_fit', label: 'Fit', max: 15 },
  { key: 'documentation_quality', label: 'Docs', max: 15 },
  { key: 'commitment_strength', label: 'Commitment', max: 15 },
];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatChf = (value) => {
  const amount = toNumber(value);
  if (!amount) return '-';
  return `CHF ${Math.round(amount).toLocaleString()}`;
};

const getScoreTone = (score) => {
  const n = toNumber(score);
  if (n >= 85) return 'text-green-700 bg-green-50 border-green-200';
  if (n >= 70) return 'text-amber-700 bg-amber-50 border-amber-200';
  return 'text-red-700 bg-red-50 border-red-200';
};

function formatNoteTimestamp(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function compareTodos(a, b) {
  const aDone = a.status === 'done' ? 1 : 0;
  const bDone = b.status === 'done' ? 1 : 0;
  if (aDone !== bDone) return aDone - bDone;
  const aTime = new Date(a.created_at || 0).getTime();
  const bTime = new Date(b.created_at || 0).getTime();
  return bTime - aTime;
}

// Compute the list of form inputs the applicant can still provide to raise their score.
// Only surfaces items where action is possible (not structural caps like CDI vs self-employed).
function computeScoreUnlocks(app, breakdown) {
  if (!app) return [];
  const financial = app.financial_profile || {};
  const budget = app.budget || {};
  const docs = app.documents || [];
  const docTypes = new Set(docs.map((d) => (d.doc_type || '').toLowerCase()));

  const unlocks = [];

  if (!breakdown.has_attestation_non_poursuite && !docTypes.has('attestation_non_poursuite')) {
    unlocks.push({
      id: 'attestation',
      label: 'Upload Attestation de non-poursuite',
      hint: 'Swiss debt-collection extract (< 3 months old)',
      delta: 12,
      category: 'Reliability',
    });
  }

  const longTermRaw = financial.long_term_intent_years;
  const longTermYears = longTermRaw === null || longTermRaw === undefined || longTermRaw === '' ? null : Number(longTermRaw);
  if (longTermYears === null || !Number.isFinite(longTermYears)) {
    unlocks.push({
      id: 'long-term-intent',
      label: 'Answer planned length of stay',
      hint: 'Tenants planning 3+ years unlock the full commitment bar',
      delta: 10,
      category: 'Commitment',
    });
  } else if (longTermYears < 3) {
    const currentPts = longTermYears >= 2 ? 7 : longTermYears >= 1 ? 4 : 1;
    unlocks.push({
      id: 'long-term-intent-upgrade',
      label: 'Confirm 3+ year stay (if realistic)',
      hint: `Currently "${formatLongTermLabel(longTermYears)}" = ${currentPts} pts, max = 10 pts`,
      delta: 10 - currentPts,
      category: 'Commitment',
      soft: true,
    });
  }

  if (!budget.deposit) {
    unlocks.push({
      id: 'deposit',
      label: 'Specify deposit capability',
      hint: '"More than 3 months" unlocks the full 5 pts',
      delta: 5,
      category: 'Income',
    });
  }

  const employmentStatus = (financial.employment_status || '').toLowerCase();
  const contractTypeApplies = employmentStatus.includes('employed') && !employmentStatus.includes('self-employed') && !employmentStatus.includes('self employed');
  if (!financial.contract_type && contractTypeApplies) {
    unlocks.push({
      id: 'contract-type',
      label: 'Specify contract type',
      hint: 'Permanent (CDI) unlocks up to 10 pts of stability',
      delta: 7,
      category: 'Income',
    });
  }

  const missingEssentials = [];
  if (!docTypes.has('passport')) missingEssentials.push('Passport or ID');
  if (!docTypes.has('work_contract') && !docTypes.has('carte_de_legitimation')) {
    missingEssentials.push('Work contract (or carte de légitimation)');
  }
  if (!docTypes.has('salary_slips')) missingEssentials.push('Salary slips (last 3 months)');
  if (!docTypes.has('residence_permit')) missingEssentials.push('Residence permit');
  if (missingEssentials.length > 0) {
    unlocks.push({
      id: 'essential-docs',
      label: `Upload missing essential documents (${missingEssentials.length})`,
      hint: missingEssentials.join(', '),
      delta: missingEssentials.length >= 2 ? Math.min(10, missingEssentials.length * 3) : 3,
      category: 'Docs',
    });
  }

  if (!docTypes.has('bank_statements')) {
    unlocks.push({
      id: 'bank-statements',
      label: 'Upload bank statements (optional)',
      hint: 'Adds up to 3 pts of documentation quality',
      delta: 3,
      category: 'Docs',
      soft: true,
    });
  }

  return unlocks;
}

function formatLongTermLabel(years) {
  if (years >= 3) return '3+ years';
  if (years >= 2) return '2-3 years';
  if (years >= 1) return '1-2 years';
  return '< 1 year';
}

function buildUnlockMailto(email, name, unlocks, currentScore, unlockTotal) {
  const lines = unlocks.map((u) => `- ${u.label} (+${u.delta} pts — ${u.category})`);
  const body =
`Hi ${name},

Thanks for submitting your application. To strengthen your file for landlords, we noticed a few optional items that would significantly improve your profile score (currently ${currentScore}/100, up to +${unlockTotal} available):

${lines.join('\n')}

You can add these directly in your dashboard. Let us know if any of them do not apply to your situation.

Best regards,
Nestgva team`;
  const subject = `Completing your Nestgva application (+${unlockTotal} score available)`;
  return `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export default function AdminPanel() {
  const { t } = useLanguage();
  const { user: currentUser, setUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const enums = useEnums();
  // Server-authoritative list of application statuses; falls back to the
  // legacy constant until the /config/enums fetch resolves.
  const appStatuses = Array.isArray(enums?.application_statuses) && enums.application_statuses.length
    ? enums.application_statuses
    : APP_STATUSES_FALLBACK;
  const [stats, setStats] = useState({});
  const [applications, setApplications] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [visits, setVisits] = useState([]);
  const [users, setUsers] = useState([]);
  const [propertyManagers, setPropertyManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showDossier, setShowDossier] = useState(false);
  const [showAddApt, setShowAddApt] = useState(false);
  const [matchUserId, setMatchUserId] = useState('');
  const [aptForm, setAptForm] = useState({ title: '', address: '', neighborhood: '', rent: '', size: '', rooms: '', furnished: false, description: '', property_manager_id: '', source: '', external_url: '' });
  const [showPmDialog, setShowPmDialog] = useState(false);
  const [editingPm, setEditingPm] = useState(null); // null = creating, object = editing
  const [pmForm, setPmForm] = useState({ first_name: '', last_name: '', email: '', phone: '', company: '', notes: '' });
  const [savingPm, setSavingPm] = useState(false);
  // Home Search cockpit (dossier tab)
  const [proposalAptId, setProposalAptId] = useState('');
  const [proposalMsg, setProposalMsg] = useState('');
  const [savingProposal, setSavingProposal] = useState(false);
  // Inline edit of an existing proposal's advisor message. Only one proposal
  // is editable at a time; empty id means "nothing in edit mode".
  const [editingProposalId, setEditingProposalId] = useState('');
  const [editingProposalMsg, setEditingProposalMsg] = useState('');
  const [savingProposalEdit, setSavingProposalEdit] = useState(false);
  const [pushPmId, setPushPmId] = useState('');
  const [pushMsg, setPushMsg] = useState('');
  const [savingPush, setSavingPush] = useState(false);
  const [editingUserId, setEditingUserId] = useState('');
  const [editingUserForm, setEditingUserForm] = useState({ name: '', email: '', role: 'tenant' });
  const [savingUserId, setSavingUserId] = useState('');
  const [deletingUserId, setDeletingUserId] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [todoDraft, setTodoDraft] = useState('');
  const [savingTodo, setSavingTodo] = useState(false);
  const [updatingTodoId, setUpdatingTodoId] = useState('');
  const [emailDraft, setEmailDraft] = useState(null); // pending draft to review in modal
  const [previewEmailId, setPreviewEmailId] = useState('');

  useEffect(() => {
    const requested = new URLSearchParams(location.search).get('tab');
    if (requested === 'apartments') {
      navigate('/admin/apartments', { replace: true });
    }
  }, [location.search, navigate]);

  const activeTab = resolveAdminActiveTab(location.pathname, location.search);

  // Deep-link support: a notification or external link can open a specific
  // dossier via `?application_id=...`. We only auto-open once per URL change
  // (tracked by `openedAppIdRef`) so manual close doesn't fight the effect.
  const openedAppIdRef = useRef('');
  useEffect(() => {
    const appId = new URLSearchParams(location.search).get('application_id');
    if (!appId) {
      openedAppIdRef.current = '';
      return;
    }
    if (openedAppIdRef.current === appId) return;
    openedAppIdRef.current = appId;
    handleViewDossier(appId).catch(() => {
      // handleViewDossier already toasts on failure
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleTabChange = (value) => {
    navigate(getAdminTabPath(value));
  };

  const loadData = async () => {
    const results = await Promise.allSettled([
      api.get('/admin/stats'),
      api.get('/applications'),
      api.get('/apartments'),
      api.get('/visits'),
      api.get('/admin/users'),
      api.get('/property-managers'),
    ]);

    const [statsRes, appsRes, apartmentsRes, visitsRes, usersRes, pmsRes] = results;
    const failedSections = [];

    if (statsRes.status === 'fulfilled') {
      setStats(statsRes.value.data || {});
    } else {
      failedSections.push('stats');
    }

    if (appsRes.status === 'fulfilled') {
      setApplications(appsRes.value.data || []);
    } else {
      failedSections.push('applications');
    }

    if (apartmentsRes.status === 'fulfilled') {
      setApartments(apartmentsRes.value.data || []);
    } else {
      failedSections.push('apartments');
    }

    if (visitsRes.status === 'fulfilled') {
      setVisits(visitsRes.value.data || []);
    } else {
      failedSections.push('visits');
    }

    if (usersRes.status === 'fulfilled') {
      setUsers(usersRes.value.data || []);
    } else {
      failedSections.push('users');
    }

    if (pmsRes.status === 'fulfilled') {
      setPropertyManagers(pmsRes.value.data || []);
    } else {
      failedSections.push('property_managers');
    }

    if (failedSections.length > 0) {
      toast.error(`Some admin data failed to load: ${failedSections.join(', ')}`);
    }
    setLoading(false);
  };

  // Targeted refresh: only refetch the slices a mutation actually affects.
  // Keeps the big loadData() around for the initial mount / hard reload.
  const SLICES = {
    stats: () => api.get('/admin/stats').then((r) => setStats(r.data || {})),
    applications: () => api.get('/applications').then((r) => setApplications(r.data || [])),
    apartments: () => api.get('/apartments').then((r) => setApartments(r.data || [])),
    visits: () => api.get('/visits').then((r) => setVisits(r.data || [])),
    users: () => api.get('/admin/users').then((r) => setUsers(r.data || [])),
    pms: () => api.get('/property-managers').then((r) => setPropertyManagers(r.data || [])),
  };
  const refresh = (keys) => Promise.allSettled((keys || []).map((k) => SLICES[k]?.()));

  useEffect(() => { loadData(); }, []);

  const handleStatusChange = async (appId, status) => {
    try {
      const res = await api.patch(`/applications/${appId}/status`, { status });
      toast.success(`Status updated to ${formatStatus(status)}`);
      const pending = res?.data?.pending_email_draft;
      if (pending && pending.id) {
        setEmailDraft(pending);
      } else if (res?.data?.pending_email_draft_error) {
        toast.error(`Email draft not prepared: ${res.data.pending_email_draft_error}`);
      }
      refresh(['applications', 'stats']);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Failed to update');
    }
  };

  const handleViewDossier = async (appId) => {
    try {
      const res = await api.get(`/applications/${appId}`);
      const doc = res.data || {};
      const normalized = {
        ...doc,
        admin_notes: Array.isArray(doc.admin_notes) ? doc.admin_notes : [],
        admin_todos: Array.isArray(doc.admin_todos) ? doc.admin_todos : [],
        listing_proposals: Array.isArray(doc.listing_proposals) ? doc.listing_proposals : [],
        dossier_pushes: Array.isArray(doc.dossier_pushes) ? doc.dossier_pushes : [],
        email_drafts: Array.isArray(doc.email_drafts) ? doc.email_drafts : [],
      };
      setSelectedApp(normalized);
      setNoteDraft('');
      setTodoDraft('');
      setProposalAptId('');
      setProposalMsg('');
      setPushPmId('');
      setPushMsg('');
      setShowDossier(true);
    } catch { toast.error('Failed to load dossier'); }
  };

  const handleAddNote = async () => {
    const content = noteDraft.trim();
    if (!selectedApp?.id || !content) return;
    setSavingNote(true);
    try {
      const res = await api.post(`/applications/${selectedApp.id}/notes`, { content });
      const note = res.data;
      setSelectedApp((prev) => prev ? { ...prev, admin_notes: [...(prev.admin_notes || []), note] } : prev);
      setNoteDraft('');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!selectedApp?.id || !noteId) return;
    try {
      await api.delete(`/applications/${selectedApp.id}/notes/${noteId}`);
      setSelectedApp((prev) => prev ? { ...prev, admin_notes: (prev.admin_notes || []).filter((n) => n.id !== noteId) } : prev);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete note');
    }
  };

  const handleAddTodo = async () => {
    const content = todoDraft.trim();
    if (!selectedApp?.id || !content) return;
    setSavingTodo(true);
    try {
      const res = await api.post(`/applications/${selectedApp.id}/todos`, { content });
      const todo = res.data;
      setSelectedApp((prev) => prev ? { ...prev, admin_todos: [...(prev.admin_todos || []), todo] } : prev);
      setTodoDraft('');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save todo');
    } finally {
      setSavingTodo(false);
    }
  };

  const handleToggleTodo = async (todo) => {
    if (!selectedApp?.id || !todo?.id) return;
    const nextStatus = todo.status === 'done' ? 'open' : 'done';
    setUpdatingTodoId(todo.id);
    try {
      const res = await api.patch(`/applications/${selectedApp.id}/todos/${todo.id}`, { status: nextStatus });
      const updated = res.data;
      setSelectedApp((prev) => prev ? {
        ...prev,
        admin_todos: (prev.admin_todos || []).map((t) => t.id === todo.id ? { ...t, ...updated } : t),
      } : prev);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update todo');
    } finally {
      setUpdatingTodoId('');
    }
  };

  const handleDeleteTodo = async (todoId) => {
    if (!selectedApp?.id || !todoId) return;
    try {
      await api.delete(`/applications/${selectedApp.id}/todos/${todoId}`);
      setSelectedApp((prev) => prev ? { ...prev, admin_todos: (prev.admin_todos || []).filter((t) => t.id !== todoId) } : prev);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete todo');
    }
  };

  // ===== Home Search cockpit handlers =====
  const handleProposeListing = async () => {
    if (!selectedApp?.id || !proposalAptId) return;
    setSavingProposal(true);
    try {
      const res = await api.post(`/applications/${selectedApp.id}/listing-proposals`, {
        apartment_id: proposalAptId,
        message: proposalMsg.trim() || null,
      });
      const proposal = res.data;
      const pending = proposal?.pending_email_draft;
      const pendingError = proposal?.pending_email_draft_error;
      // Strip pending_email_draft* so they don't pollute the dossier list.
      const { pending_email_draft: _ignored, pending_email_draft_error: _ignored2, ...clean } = proposal || {};
      setSelectedApp(prev => prev ? { ...prev, listing_proposals: [clean, ...(prev.listing_proposals || [])] } : prev);
      setProposalAptId('');
      setProposalMsg('');
      toast.success(t('admin.email.proposedNeedsSend') || 'Listing proposed. Review and send the email to notify the applicant.');
      if (pending && pending.id) setEmailDraft(pending);
      else if (pendingError) toast.error(`Email draft not prepared: ${pendingError}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to propose listing');
    } finally {
      setSavingProposal(false);
    }
  };

  const handleWithdrawProposal = async (proposalId) => {
    if (!selectedApp?.id || !proposalId) return;
    try {
      const res = await api.patch(`/applications/${selectedApp.id}/listing-proposals/${proposalId}`, { status: 'withdrawn' });
      const updated = res.data;
      const pending = updated?.pending_email_draft;
      const pendingError = updated?.pending_email_draft_error;
      const { pending_email_draft: _ignored, pending_email_draft_error: _ignored2, ...clean } = updated || {};
      setSelectedApp(prev => prev ? {
        ...prev,
        listing_proposals: (prev.listing_proposals || []).map(p => p.id === proposalId ? clean : p),
      } : prev);
      if (pending && pending.id) setEmailDraft(pending);
      else if (pendingError) toast.error(`Email draft not prepared: ${pendingError}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to withdraw');
    }
  };

  const handleStartEditProposal = (proposal) => {
    setEditingProposalId(proposal.id);
    setEditingProposalMsg(proposal.message || '');
  };

  const handleCancelEditProposal = () => {
    setEditingProposalId('');
    setEditingProposalMsg('');
  };

  const handleSaveProposalEdit = async (proposalId) => {
    if (!selectedApp?.id || !proposalId) return;
    setSavingProposalEdit(true);
    try {
      const res = await api.patch(`/applications/${selectedApp.id}/listing-proposals/${proposalId}`, {
        message: editingProposalMsg.trim() || null,
      });
      const updated = res.data;
      const { pending_email_draft: _ignored, pending_email_draft_error: _ignored2, ...clean } = updated || {};
      setSelectedApp(prev => prev ? {
        ...prev,
        listing_proposals: (prev.listing_proposals || []).map(p => p.id === proposalId ? clean : p),
      } : prev);
      setEditingProposalId('');
      setEditingProposalMsg('');
      toast.success('Proposal updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSavingProposalEdit(false);
    }
  };

  const handleDeleteProposal = async (proposalId) => {
    if (!selectedApp?.id || !proposalId) return;
    try {
      await api.delete(`/applications/${selectedApp.id}/listing-proposals/${proposalId}`);
      setSelectedApp(prev => prev ? {
        ...prev,
        listing_proposals: (prev.listing_proposals || []).filter(p => p.id !== proposalId),
      } : prev);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const handlePushDossier = async () => {
    if (!selectedApp?.id || !pushPmId) return;
    setSavingPush(true);
    try {
      const res = await api.post(`/applications/${selectedApp.id}/dossier-pushes`, {
        property_manager_id: pushPmId,
        message: pushMsg.trim() || null,
      });
      const push = res.data;
      const pending = push?.pending_email_draft;
      const pendingError = push?.pending_email_draft_error;
      const { pending_email_draft: _ignored, pending_email_draft_error: _ignored2, ...clean } = push || {};
      setSelectedApp(prev => prev ? { ...prev, dossier_pushes: [clean, ...(prev.dossier_pushes || [])] } : prev);
      setPushPmId('');
      setPushMsg('');
      toast.success('Dossier push recorded');
      if (pending && pending.id) setEmailDraft(pending);
      else if (pendingError) toast.error(`Email draft not prepared: ${pendingError}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to push dossier');
    } finally {
      setSavingPush(false);
    }
  };

  const handleUpdatePushStatus = async (pushId, status) => {
    if (!selectedApp?.id || !pushId) return;
    try {
      const res = await api.patch(`/applications/${selectedApp.id}/dossier-pushes/${pushId}`, { status });
      const updated = res.data;
      setSelectedApp(prev => prev ? {
        ...prev,
        dossier_pushes: (prev.dossier_pushes || []).map(p => p.id === pushId ? updated : p),
      } : prev);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDeletePush = async (pushId) => {
    if (!selectedApp?.id || !pushId) return;
    try {
      await api.delete(`/applications/${selectedApp.id}/dossier-pushes/${pushId}`);
      setSelectedApp(prev => prev ? {
        ...prev,
        dossier_pushes: (prev.dossier_pushes || []).filter(p => p.id !== pushId),
      } : prev);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const handlePreviewDocument = (docId) => {
    window.open(`${getBackendOrigin()}/api/documents/${docId}/download`, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await fetch(`${getBackendOrigin()}/api/documents/${doc.id}/download`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed download');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = doc.original_filename || `document-${doc.id}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download document');
    }
  };

  const handleCreateApartment = async () => {
    try {
      await api.post('/apartments', buildApartmentCreatePayload(aptForm));
      toast.success('Apartment created');
      setShowAddApt(false);
      setAptForm({ title: '', address: '', neighborhood: '', rent: '', size: '', rooms: '', furnished: false, description: '', property_manager_id: '', source: '', external_url: '' });
      refresh(['apartments', 'stats']);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || 'Failed to create');
    }
  };

  // ===== Property Manager handlers =====
  const openCreatePm = () => {
    setEditingPm(null);
    setPmForm({ first_name: '', last_name: '', email: '', phone: '', company: '', notes: '' });
    setShowPmDialog(true);
  };

  const openEditPm = (pm) => {
    setEditingPm(pm);
    setPmForm({
      first_name: pm.first_name || '',
      last_name: pm.last_name || '',
      email: pm.email || '',
      phone: pm.phone || '',
      company: pm.company || '',
      notes: pm.notes || '',
    });
    setShowPmDialog(true);
  };

  const handleSavePm = async () => {
    const first = pmForm.first_name.trim();
    const last = pmForm.last_name.trim();
    const email = pmForm.email.trim();
    if (!first || !last || !email) {
      toast.error('First name, last name and email are required');
      return;
    }
    setSavingPm(true);
    try {
      const payload = {
        first_name: first,
        last_name: last,
        email,
        phone: pmForm.phone.trim() || null,
        company: pmForm.company.trim() || null,
        notes: pmForm.notes.trim() || null,
      };
      if (editingPm?.id) {
        await api.patch(`/property-managers/${editingPm.id}`, payload);
        toast.success('Property manager updated');
      } else {
        await api.post('/property-managers', payload);
        toast.success('Property manager added');
      }
      setShowPmDialog(false);
      refresh(['pms', 'apartments']);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingPm(false);
    }
  };

  const handleArchivePm = async (pmId) => {
    try {
      await api.delete(`/property-managers/${pmId}`);
      toast.success('Property manager archived');
      refresh(['pms', 'apartments']);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to archive');
    }
  };


  const handleDeleteApartment = async (aptId) => {
    try { await api.delete(`/apartments/${aptId}`); toast.success('Deleted'); refresh(['apartments', 'stats']); }
    catch { toast.error('Failed to delete'); }
  };

  const handleVisitStatus = async (visitId, status) => {
    try {
      const res = await api.patch(`/visits/${visitId}/status`, { status });
      toast.success('Visit updated');
      const pending = res?.data?.pending_email_draft;
      if (pending && pending.id) setEmailDraft(pending);
      else if (res?.data?.pending_email_draft_error) {
        toast.error(`Email draft not prepared: ${res.data.pending_email_draft_error}`);
      }
      refresh(['visits', 'applications', 'stats']);
    } catch { toast.error('Failed to update'); }
  };

  const handleStartEditUser = (targetUser) => {
    setEditingUserId(targetUser.user_id);
    setEditingUserForm({
      name: targetUser.name || '',
      email: targetUser.email || '',
      role: targetUser.role || 'tenant',
    });
  };

  const handleCancelEditUser = () => {
    setEditingUserId('');
    setEditingUserForm({ name: '', email: '', role: 'tenant' });
  };

  const handleSaveUser = async (userId) => {
    try {
      setSavingUserId(userId);
      const payload = {
        name: editingUserForm.name.trim(),
        email: editingUserForm.email.trim().toLowerCase(),
        role: editingUserForm.role,
      };
      const res = await api.patch(`/admin/users/${userId}`, payload);
      if (currentUser?.user_id === userId) {
        setUser(res.data);
      }
      toast.success('User updated');
      handleCancelEditUser();
      await refresh(['users']);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingUserId('');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      setDeletingUserId(userId);
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      if (editingUserId === userId) {
        handleCancelEditUser();
      }
      await refresh(['users', 'applications', 'stats']);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingUserId('');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F5F3EF]" data-testid="admin-loading"><div className="w-6 h-6 border-2 border-[#0B3D2E] border-t-transparent rounded-full animate-spin" /></div>;

  const tenants = users.filter(u => u.role === 'tenant');
  const sortedApplications = [...applications].sort((a, b) => (b.application_score ?? 0) - (a.application_score ?? 0));
  const score = toNumber(selectedApp?.application_score);
  const scoreTone = getScoreTone(score);
  const breakdown = selectedApp?.application_score_breakdown || {};
  const income = toNumber(selectedApp?.financial_profile?.monthly_income);
  const targetRent = toNumber(selectedApp?.budget?.target_rent || selectedApp?.budget?.max_rent);
  const rentRatio = income > 0 && targetRent > 0 ? (income / targetRent) : 0;
  const docsCount = selectedApp?.documents?.length || 0;
  const scoreBars = SCORE_CATEGORY_CONFIG.map((item) => {
    const value = toNumber(breakdown[item.key]);
    const pct = Math.max(0, Math.min(100, (value / item.max) * 100));
    return { ...item, value, pct };
  });
  const residencyStatus = (selectedApp?.financial_profile?.residency_status || '').toLowerCase();
  const fragileResidency = residencyStatus.includes('other') || residencyStatus.includes('pending') || residencyStatus.includes('permit l');
  const hasAttestation = !!breakdown.has_attestation_non_poursuite;
  const riskIndicators = [];
  if (rentRatio > 0 && rentRatio < 2.5) riskIndicators.push(`LOW INCOME COVERAGE (${rentRatio.toFixed(1)}x)`);
  // NO GUARANTOR is only a risk when income coverage is weak OR residency is fragile.
  if (selectedApp?.budget && !selectedApp.budget.has_guarantor && (rentRatio > 0 && rentRatio < 3.0 || fragileResidency)) {
    riskIndicators.push('NO GUARANTOR');
  }
  if (!hasAttestation) riskIndicators.push('NO ATTESTATION DE NON POURSUITE');
  if (docsCount < 3) riskIndicators.push(`LIMITED DOCUMENTS (${docsCount})`);
  const topRisks = riskIndicators.slice(0, 3);

  const recommendation = score < 50
    ? { label: 'REJECT', reason: 'Score below minimum threshold' }
    : (rentRatio > 0 && rentRatio < 2.5)
      ? { label: 'HOLD', reason: 'Income ratio below threshold' }
      : (score >= 70 && topRisks.length <= 1)
        ? { label: 'APPROVE', reason: 'Risk/score profile acceptable' }
        : { label: 'HOLD', reason: 'Borderline profile needs manual review' };

  const scoreUnlocks = computeScoreUnlocks(selectedApp, breakdown);
  const unlockTotal = scoreUnlocks.reduce((sum, item) => sum + item.delta, 0);
  const applicantEmail = selectedApp?.user?.email || '';
  const applicantName = selectedApp?.user?.name || 'there';
  const unlockMailto = scoreUnlocks.length > 0 && applicantEmail
    ? buildUnlockMailto(applicantEmail, applicantName, scoreUnlocks, score, unlockTotal)
    : null;

  const rankingTopThree = sortedApplications.slice(0, 3);
  const selectedRank = selectedApp ? sortedApplications.findIndex((a) => a.id === selectedApp.id) : -1;

  const applyDecisionStatus = async (status) => {
    if (!selectedApp?.id) return;
    await handleStatusChange(selectedApp.id, status);
    setSelectedApp((prev) => (prev ? { ...prev, status } : prev));
  };

  return (
    <div className="min-h-screen bg-[#F5F3EF]" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-heading text-2xl sm:text-3xl text-[#2B2B2B] tracking-tight mb-4 sm:mb-6">{t('admin.title')}</h1>
        <AdminNavTabs activeTab={activeTab} />

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">

          {/* Dashboard */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats">
              {[
                { label: t('admin.totalApps'), value: stats.total_applications, icon: Users, color: 'text-[#0B3D2E]' },
                { label: t('admin.pending'), value: stats.submitted, icon: FileText, color: 'text-blue-600' },
                { label: t('admin.inSearch'), value: stats.in_search, icon: Link2, color: 'text-amber-600' },
                { label: t('admin.matched'), value: stats.matched, icon: CheckCircle, color: 'text-emerald-600' },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-lg border border-[#2B2B2B]/10 p-6 shadow-sm" data-testid={`stat-card-${i}`}>
                  <s.icon className={`w-5 h-5 ${s.color} mb-3`} />
                  <p className="text-2xl font-heading text-[#2B2B2B]">{s.value || 0}</p>
                  <p className="text-xs text-[#5a5a5a] mt-1">{s.label}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="bg-white rounded-lg border border-[#2B2B2B]/10 p-6 shadow-sm" data-testid="stat-apartments">
                <Home className="w-5 h-5 text-[#0B3D2E] mb-3" />
                <p className="text-2xl font-heading text-[#2B2B2B]">{stats.total_apartments || 0}</p>
                <p className="text-xs text-[#5a5a5a] mt-1">Total Apartments</p>
              </div>
              <div className="bg-white rounded-lg border border-[#2B2B2B]/10 p-6 shadow-sm" data-testid="stat-visits">
                <Calendar className="w-5 h-5 text-amber-600 mb-3" />
                <p className="text-2xl font-heading text-[#2B2B2B]">{stats.pending_visits || 0}</p>
                <p className="text-xs text-[#5a5a5a] mt-1">Pending Visits</p>
              </div>
            </div>
          </TabsContent>

          {/* Applications */}
          <TabsContent value="applications">
            <div className="bg-white rounded-lg border border-[#2B2B2B]/10 shadow-sm overflow-hidden" data-testid="applications-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F5F3EF] text-[10px] uppercase tracking-wider text-[#5a5a5a]">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">Applicant</th>
                      <th className="text-left px-4 py-3 font-semibold">Status</th>
                      <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Score</th>
                      <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Rent</th>
                      <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Next to-do</th>
                      <th className="text-right px-4 py-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#2B2B2B]/5">
                    {sortedApplications.map(app => {
                      const summary = buildApplicantSummary(app);
                      const openTodo = latestOpenTodo(app);
                      return (
                      <tr key={app.id} className="hover:bg-[#F5F3EF]/50 transition-colors" data-testid={`app-row-${app.id}`}>
                        <td className="px-4 py-3 max-w-[260px]">
                          <p className="font-medium text-[#2B2B2B] break-words">{app.user?.name || app.user?.email || '-'}</p>
                          <p className="text-[10px] text-[#5a5a5a] break-all">{app.user?.email}</p>
                          {summary && (
                            <p className="text-[11px] text-[#2B2B2B] mt-1 truncate" title={summary} data-testid={`app-summary-${app.id}`}>
                              {summary}
                            </p>
                          )}
                          {/* Mobile fallback: show the open todo below the summary since the
                              dedicated column is hidden under lg. */}
                          {openTodo && (
                            <p className="text-[10px] text-amber-700 mt-1 truncate lg:hidden" title={openTodo.content}>
                              {'\u2022 '}{openTodo.content}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Select value={app.status} onValueChange={v => handleStatusChange(app.id, v)}>
                            <SelectTrigger className="h-7 w-[130px] text-xs" data-testid={`status-select-${app.id}`}>
                              <Badge className={`${STATUS_COLORS[app.status] || ''} border-0 text-[10px]`}>{formatStatus(app.status)}</Badge>
                            </SelectTrigger>
                            <SelectContent>
                              {appStatuses.map(s => (
                                <SelectItem key={s} value={s} className="text-xs">{formatStatus(s)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <Badge className="bg-[#0B3D2E]/10 text-[#0B3D2E] border-0 text-[10px]">
                            {app.application_score ?? 0}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-[#5a5a5a] whitespace-nowrap">
                          {app.budget?.target_rent ? `CHF ${Number(app.budget.target_rent).toLocaleString()} / mo` : '-'}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell max-w-[240px]" data-testid={`app-next-todo-${app.id}`}>
                          {openTodo ? (
                            <div className="truncate text-xs text-[#2B2B2B]" title={openTodo.content}>
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 mr-1.5 align-middle" />
                              {openTodo.content}
                            </div>
                          ) : (
                            <span className="text-[11px] text-[#5a5a5a]">{'\u2014'}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button size="sm" variant="ghost" className="text-xs text-[#0B3D2E]" onClick={() => handleViewDossier(app.id)} data-testid={`view-dossier-${app.id}`}>
                            <Eye className="w-3 h-3 mr-1" /> {t('admin.review')}
                          </Button>
                        </td>
                      </tr>
                      );
                    })}
                    {sortedApplications.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-12 text-center text-[#5a5a5a]">No applications yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Apartments — dedicated CRM at /admin/apartments */}
          <TabsContent value="apartments" />

          {/* Visits */}
          <TabsContent value="visits">
            <div className="bg-white rounded-lg border border-[#2B2B2B]/10 shadow-sm overflow-hidden" data-testid="visits-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[820px]">
                  <thead className="bg-[#F5F3EF] text-[10px] uppercase tracking-wider text-[#5a5a5a]">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">Tenant</th>
                      <th className="text-left px-4 py-3 font-semibold">Apartment</th>
                      <th className="text-left px-4 py-3 font-semibold">Organized by</th>
                      <th className="text-left px-4 py-3 font-semibold">Date</th>
                      <th className="text-left px-4 py-3 font-semibold">Status</th>
                      <th className="text-right px-4 py-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#2B2B2B]/5">
                    {visits.map(v => {
                      const pm = v.apartment?.property_manager;
                      return (
                      <tr key={v.id} className="hover:bg-[#F5F3EF]/50" data-testid={`visit-row-${v.id}`}>
                        <td className="px-4 py-3">{v.user?.name || v.user?.email || '-'}</td>
                        <td className="px-4 py-3">{v.apartment?.title || '-'}</td>
                        <td className="px-4 py-3">
                          {pm ? (
                            <Badge className="bg-indigo-50 text-indigo-700 border-0 text-[10px]" title={pm.email || ''}>
                              {pm.company || `${pm.first_name} ${pm.last_name}`}
                            </Badge>
                          ) : (
                            <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px]">Nest</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">{v.requested_date}</td>
                        <td className="px-4 py-3">
                          <Badge className={`${STATUS_COLORS[v.status] || ''} border-0 text-[10px]`}>{v.status}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Select value={v.status} onValueChange={s => handleVisitStatus(v.id, s)}>
                            <SelectTrigger className="h-7 w-[110px] text-xs" data-testid={`visit-status-${v.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{VISIT_STATUSES.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                      </tr>
                    );})}
                    {visits.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-[#5a5a5a]">No visits yet</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Property Managers */}
          <TabsContent value="property_managers">
            <div className="flex justify-end mb-4">
              <Button className="bg-[#0B3D2E] hover:bg-[#0f523e] text-white text-sm w-full sm:w-auto" onClick={openCreatePm} data-testid="add-pm-btn">
                <Plus className="w-4 h-4 mr-1" /> {t('admin.addPropertyManager')}
              </Button>
            </div>
            <div className="bg-white rounded-lg border border-[#2B2B2B]/10 shadow-sm overflow-hidden" data-testid="pms-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[800px]">
                  <thead className="bg-[#F5F3EF] text-[10px] uppercase tracking-wider text-[#5a5a5a]">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">Name</th>
                      <th className="text-left px-4 py-3 font-semibold">Company</th>
                      <th className="text-left px-4 py-3 font-semibold">Email</th>
                      <th className="text-left px-4 py-3 font-semibold">Phone</th>
                      <th className="text-left px-4 py-3 font-semibold">Listings</th>
                      <th className="text-right px-4 py-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#2B2B2B]/5">
                    {propertyManagers.map(pm => (
                      <tr key={pm.id} className="hover:bg-[#F5F3EF]/50" data-testid={`pm-row-${pm.id}`}>
                        <td className="px-4 py-3 text-[#2B2B2B]">{pm.first_name} {pm.last_name}</td>
                        <td className="px-4 py-3 text-[#5a5a5a]">{pm.company || '-'}</td>
                        <td className="px-4 py-3 text-[#5a5a5a]">
                          <a href={`mailto:${pm.email}`} className="text-[#0B3D2E] hover:underline">{pm.email}</a>
                        </td>
                        <td className="px-4 py-3 text-[#5a5a5a]">{pm.phone || '-'}</td>
                        <td className="px-4 py-3 text-[#5a5a5a]">{pm.listings_count || 0}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="outline" className="text-xs" onClick={() => openEditPm(pm)} data-testid={`edit-pm-${pm.id}`}>
                              Edit
                            </Button>
                            <Button size="sm" variant="ghost" className="text-xs text-red-500" onClick={() => handleArchivePm(pm.id)} data-testid={`archive-pm-${pm.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {propertyManagers.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-12 text-center text-[#5a5a5a]">No property managers yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Users */}
          <TabsContent value="users">
            <div className="bg-white rounded-lg border border-[#2B2B2B]/10 shadow-sm overflow-hidden" data-testid="users-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
                  <thead className="bg-[#F5F3EF] text-[10px] uppercase tracking-wider text-[#5a5a5a]">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">{t('admin.userName')}</th>
                      <th className="text-left px-4 py-3 font-semibold">{t('admin.userEmail')}</th>
                      <th className="text-left px-4 py-3 font-semibold">{t('admin.userRole')}</th>
                      <th className="text-right px-4 py-3 font-semibold">{t('admin.userActions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#2B2B2B]/5">
                    {users.map((u) => {
                      const isEditing = editingUserId === u.user_id;
                      return (
                        <tr key={u.user_id} className="hover:bg-[#F5F3EF]/50" data-testid={`user-row-${u.user_id}`}>
                          <td className="px-4 py-3">
                            {isEditing ? (
                              <Input
                                value={editingUserForm.name}
                                onChange={(e) => setEditingUserForm((prev) => ({ ...prev, name: e.target.value }))}
                                className="h-8 text-xs"
                                data-testid={`user-name-input-${u.user_id}`}
                              />
                            ) : (
                              <p className="text-[#2B2B2B]">{u.name || '-'}</p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {isEditing ? (
                              <Input
                                type="email"
                                value={editingUserForm.email}
                                onChange={(e) => setEditingUserForm((prev) => ({ ...prev, email: e.target.value }))}
                                className="h-8 text-xs"
                                data-testid={`user-email-input-${u.user_id}`}
                              />
                            ) : (
                              <p className="text-[#5a5a5a]">{u.email}</p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {isEditing ? (
                              <Select value={editingUserForm.role} onValueChange={(value) => setEditingUserForm((prev) => ({ ...prev, role: value }))}>
                                <SelectTrigger className="h-8 w-[120px] text-xs" data-testid={`user-role-select-${u.user_id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="tenant" className="text-xs">tenant</SelectItem>
                                  <SelectItem value="admin" className="text-xs">admin</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <Badge className={`${u.role === 'admin' ? 'bg-[#0B3D2E]/10 text-[#0B3D2E]' : 'bg-gray-100 text-gray-700'} border-0 text-[10px]`}>
                                {u.role}
                              </Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {isEditing ? (
                              <div className="inline-flex items-center gap-2">
                                <Button
                                  size="sm"
                                  className="h-8 text-xs bg-[#0B3D2E] text-white"
                                  onClick={() => handleSaveUser(u.user_id)}
                                  disabled={savingUserId === u.user_id}
                                  data-testid={`save-user-${u.user_id}`}
                                >
                                  {savingUserId === u.user_id ? t('common.loading') : t('common.save')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 text-xs"
                                  onClick={handleCancelEditUser}
                                  data-testid={`cancel-edit-user-${u.user_id}`}
                                >
                                  {t('common.cancel')}
                                </Button>
                              </div>
                            ) : (
                              <div className="inline-flex flex-wrap justify-end items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 text-xs border-[#0B3D2E] text-[#0B3D2E]"
                                  onClick={() => handleStartEditUser(u)}
                                  data-testid={`edit-user-${u.user_id}`}
                                >
                                  {t('admin.editUser')}
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 text-xs text-red-600 hover:text-red-700"
                                      data-testid={`delete-user-trigger-${u.user_id}`}
                                    >
                                      {t('common.delete')}
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>{t('admin.deleteUserTitle')}</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        {t('admin.deleteUserDesc')}
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => handleDeleteUser(u.user_id)}
                                        disabled={deletingUserId === u.user_id}
                                        className="bg-red-600 hover:bg-red-700"
                                        data-testid={`delete-user-confirm-${u.user_id}`}
                                      >
                                        {deletingUserId === u.user_id ? t('common.loading') : t('common.delete')}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {users.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-12 text-center text-[#5a5a5a]">{t('admin.noUsers')}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dossier Dialog */}
      <Dialog
        open={showDossier}
        onOpenChange={(next) => {
          setShowDossier(next);
          if (!next) {
            // Drop ?application_id=... so another notification click on the
            // same dossier can re-open it, and so the URL stays clean.
            const params = new URLSearchParams(location.search);
            if (params.has('application_id')) {
              params.delete('application_id');
              const query = params.toString();
              navigate(`/admin${query ? `?${query}` : ''}`, { replace: true });
            }
          }
        }}
      >
        <DialogContent className="flex flex-col w-[100vw] max-w-none h-[100vh] max-h-none min-h-0 overflow-hidden p-0 gap-0 sm:w-[98vw] sm:h-[96vh]" data-testid="dossier-dialog">
          {selectedApp && (
            <div className="flex flex-col flex-1 min-h-0 bg-[#F5F3EF]">
              <div className="sticky top-0 z-20 border-b border-[#2B2B2B]/10 bg-white px-3 sm:px-5 py-3">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <DialogTitle className="font-heading text-base sm:text-lg text-[#2B2B2B] truncate">{selectedApp.user?.name || selectedApp.user?.email || 'Applicant'}</DialogTitle>
                      <Badge className={`${scoreTone} border text-xs shrink-0`} data-testid="dossier-score-badge">
                        {score.toFixed(1)} / 100
                      </Badge>
                    </div>
                    <p className="text-[11px] text-[#5a5a5a] mt-1 truncate">
                      {selectedApp.user?.email}
                      {selectedApp.user?.phone ? ` · ${selectedApp.user.phone}` : ''}
                    </p>
                    <p className="text-[11px] text-[#2B2B2B] mt-1 flex items-center gap-2 flex-wrap">
                      <span>{formatChf(income)} income</span>
                      <span>|</span>
                      <span>Target {formatChf(targetRent)}</span>
                      <span>|</span>
                      <span className={rentRatio > 0 && rentRatio < 2.5 ? 'text-red-700 font-semibold' : ''}>
                        Ratio {rentRatio > 0 ? rentRatio.toFixed(1) : '-'}
                      </span>
                      {topRisks[0] && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 px-2 py-0.5 text-[10px]">
                          <AlertTriangle className="w-3 h-3" />
                          {topRisks[0]}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2" data-testid="dossier-decision-actions">
                    {/* Suggested next steps - quick-action buttons with semantic colors. */}
                    {(SUGGESTED_NEXT[selectedApp.status] || []).map((next) => {
                      const meta = DECISION_BUTTON_META[next] || { label: formatStatus(next), className: 'h-8 text-xs border-[#2B2B2B]/30 text-[#2B2B2B]', variant: 'outline' };
                      return (
                        <Button
                          key={next}
                          size="sm"
                          variant={meta.variant}
                          className={meta.className}
                          onClick={() => applyDecisionStatus(next)}
                          data-testid={`dossier-action-${next}`}
                        >
                          {meta.label}
                        </Button>
                      );
                    })}
                    {/* Full status picker - any state reachable from any state. */}
                    <Select value={selectedApp.status} onValueChange={(v) => { if (v !== selectedApp.status) applyDecisionStatus(v); }}>
                      <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="dossier-status-select">
                        <SelectValue placeholder="Change status..." />
                      </SelectTrigger>
                      <SelectContent>
                        {appStatuses.map(s => (
                          <SelectItem key={s} value={s} className="text-xs" disabled={s === selectedApp.status}>
                            {formatStatus(s)}{s === selectedApp.status ? ' (current)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs border-[#2B2B2B]/20 text-[#2B2B2B]"
                      onClick={() => setShowDossier(false)}
                      data-testid="dossier-close-btn"
                      aria-label="Close dossier"
                    >
                      <X className="w-3.5 h-3.5 mr-1" />
                      Close
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 sm:p-4">
                <div className="grid grid-cols-1 xl:grid-cols-5 gap-3">
                  <div className="order-2 xl:order-1 xl:col-span-2 space-y-3">
                    <div className="bg-white border border-[#2B2B2B]/10 rounded-md p-3">
                      <p className="text-[10px] uppercase tracking-wider text-[#5a5a5a] font-semibold mb-2">Applicant snapshot</p>
                      <div className="space-y-1 text-xs text-[#2B2B2B]">
                        <p className="font-medium text-sm">{selectedApp.user?.name || '-'}</p>
                        <p><span className="text-[#5a5a5a]">Phone:</span> {selectedApp.user?.phone || '-'}</p>
                        <p>{selectedApp.financial_profile?.residency_status || '-'} | {selectedApp.financial_profile?.employer || '-'} | {selectedApp.financial_profile?.job_title || '-'}</p>
                        <p><span className="text-[#5a5a5a]">Income:</span> {formatChf(income)}</p>
                        <p><span className="text-[#5a5a5a]">Target rent:</span> {formatChf(targetRent)}</p>
                        <p><span className="text-[#5a5a5a]">Ratio:</span> <span className={rentRatio > 0 && rentRatio < 2.5 ? 'text-red-700 font-semibold' : ''}>{rentRatio > 0 ? rentRatio.toFixed(1) : '-'}</span></p>
                        <p><span className="text-[#5a5a5a]">Guarantor:</span> {selectedApp.budget?.has_guarantor ? 'Yes' : 'No'}</p>
                        <p><span className="text-[#5a5a5a]">Search:</span> {selectedApp.search_criteria?.property_type || '-'} | {selectedApp.search_criteria?.num_rooms || '-'} rooms</p>
                        <p><span className="text-[#5a5a5a]">Areas:</span> {(selectedApp.search_criteria?.neighborhoods || []).join(' / ') || '-'}</p>
                        <p><span className="text-[#5a5a5a]">Min size:</span> {selectedApp.search_criteria?.min_size ? `${selectedApp.search_criteria.min_size}m²` : '-'}</p>
                      </div>
                    </div>

                    <div className="bg-white border border-[#2B2B2B]/10 rounded-md p-3" data-testid="admin-notes-panel">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] uppercase tracking-wider text-[#5a5a5a] font-semibold">
                          Admin notes ({(selectedApp.admin_notes || []).length})
                        </p>
                      </div>
                      <div className="space-y-2 mb-3">
                        {(selectedApp.admin_notes || []).length === 0 && (
                          <p className="text-xs text-[#5a5a5a]">No notes yet. Add your first one below.</p>
                        )}
                        {(selectedApp.admin_notes || []).map((note) => (
                          <div key={note.id} className="rounded-md border border-[#2B2B2B]/10 bg-[#F8F7F4] p-2.5" data-testid={`admin-note-${note.id}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-xs text-[#2B2B2B] whitespace-pre-wrap break-words">{note.content}</p>
                                <p className="text-[10px] text-[#5a5a5a] mt-1">
                                  {note.author_name || 'Admin'} · {formatNoteTimestamp(note.created_at)}
                                </p>
                              </div>
                              <button
                                onClick={() => handleDeleteNote(note.id)}
                                className="shrink-0 text-[#5a5a5a] hover:text-red-600"
                                aria-label="Delete note"
                                data-testid={`delete-note-${note.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="space-y-2">
                        <Textarea
                          value={noteDraft}
                          onChange={(e) => setNoteDraft(e.target.value)}
                          placeholder="Add a note about this applicant (visible to admins only)..."
                          className="text-xs min-h-[72px]"
                          maxLength={2000}
                          data-testid="admin-note-input"
                        />
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <p className="text-[10px] text-[#5a5a5a]">{noteDraft.length}/2000 · Timestamped and attributed to you.</p>
                          <Button
                            size="sm"
                            className="h-7 text-[11px] px-3 bg-[#0B3D2E] text-white hover:bg-[#0f523e] disabled:opacity-50 sm:shrink-0"
                            onClick={handleAddNote}
                            disabled={savingNote || !noteDraft.trim()}
                            data-testid="add-note-btn"
                          >
                            {savingNote ? 'Saving...' : 'Add note'}
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border border-[#2B2B2B]/10 rounded-md p-3" data-testid="admin-todos-panel">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] uppercase tracking-wider text-[#5a5a5a] font-semibold">
                          To-do list ({(selectedApp.admin_todos || []).filter((t) => t.status !== 'done').length} open)
                        </p>
                        <p className="text-[10px] text-[#5a5a5a]">
                          {(selectedApp.admin_todos || []).filter((t) => t.status === 'done').length} done
                        </p>
                      </div>
                      <div className="space-y-1.5 mb-3">
                        {(selectedApp.admin_todos || []).length === 0 && (
                          <p className="text-xs text-[#5a5a5a]">No tasks yet. Add one below to remember what's next for this applicant.</p>
                        )}
                        {(selectedApp.admin_todos || [])
                          .slice()
                          .sort(compareTodos)
                          .map((todo) => {
                            const isDone = todo.status === 'done';
                            const isUpdating = updatingTodoId === todo.id;
                            return (
                              <div
                                key={todo.id}
                                className={`flex items-start gap-2 rounded-md border px-2 py-1.5 ${isDone ? 'border-[#2B2B2B]/5 bg-[#F8F7F4]' : 'border-[#2B2B2B]/10 bg-white'}`}
                                data-testid={`admin-todo-${todo.id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isDone}
                                  disabled={isUpdating}
                                  onChange={() => handleToggleTodo(todo)}
                                  className="mt-0.5 h-3.5 w-3.5 shrink-0 cursor-pointer accent-[#0B3D2E]"
                                  aria-label={isDone ? 'Mark as open' : 'Mark as done'}
                                  data-testid={`toggle-todo-${todo.id}`}
                                />
                                <div className="min-w-0 flex-1">
                                  <p className={`text-xs break-words ${isDone ? 'line-through text-[#5a5a5a]' : 'text-[#2B2B2B]'}`}>
                                    {todo.content}
                                  </p>
                                  <p className="text-[10px] text-[#5a5a5a] mt-0.5">
                                    {todo.author_name || 'Admin'} · Added {formatNoteTimestamp(todo.created_at)}
                                    {isDone && todo.completed_at ? ` · Done ${formatNoteTimestamp(todo.completed_at)}` : ''}
                                  </p>
                                </div>
                                <button
                                  onClick={() => handleDeleteTodo(todo.id)}
                                  className="shrink-0 text-[#5a5a5a] hover:text-red-600"
                                  aria-label="Delete task"
                                  data-testid={`delete-todo-${todo.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            );
                          })}
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <Input
                          value={todoDraft}
                          onChange={(e) => setTodoDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !savingTodo && todoDraft.trim()) {
                              e.preventDefault();
                              handleAddTodo();
                            }
                          }}
                          placeholder="e.g. Follow up with Tafsir on attestation"
                          className="text-xs h-9 sm:flex-1"
                          maxLength={500}
                          data-testid="admin-todo-input"
                        />
                        <Button
                          size="sm"
                          className="h-9 text-[11px] px-3 bg-[#0B3D2E] text-white hover:bg-[#0f523e] disabled:opacity-50 shrink-0 w-full sm:w-auto"
                          onClick={handleAddTodo}
                          disabled={savingTodo || !todoDraft.trim()}
                          data-testid="add-todo-btn"
                        >
                          {savingTodo ? 'Saving...' : 'Add task'}
                        </Button>
                      </div>
                    </div>

                    <details className="bg-white border border-[#2B2B2B]/10 rounded-md p-3">
                      <summary className="cursor-pointer text-[11px] uppercase tracking-wider text-[#5a5a5a] font-semibold">Timeline & metadata</summary>
                      <div className="mt-2 grid grid-cols-1 gap-3 text-xs">
                        <div>
                          <p className="text-[10px] uppercase text-[#5a5a5a]">Timeline</p>
                          <p className="text-[#2B2B2B] mt-1">Created: {selectedApp.created_at ? new Date(selectedApp.created_at).toLocaleDateString() : '-'}</p>
                          <p className="text-[#2B2B2B] mt-1">Updated: {selectedApp.updated_at ? new Date(selectedApp.updated_at).toLocaleDateString() : '-'}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase text-[#5a5a5a]">Communication logs</p>
                          <p className="text-[#5a5a5a] mt-1">Not captured yet in this panel.</p>
                        </div>
                      </div>
                    </details>
                  </div>

                  <div className="order-1 xl:order-2 xl:col-span-3 space-y-3">
                    <div className="bg-white border border-[#2B2B2B]/10 rounded-md p-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] uppercase tracking-wider text-[#5a5a5a] font-semibold">Decision engine</p>
                        <p className="text-[11px] text-[#5a5a5a]">Status: <span className="text-[#2B2B2B]">{formatStatus(selectedApp.status) || '-'}</span></p>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <p className="text-[11px] font-semibold text-[#2B2B2B]">Score breakdown</p>
                          {scoreBars.map((row) => (
                            <ScoreBar key={row.key} label={row.label} value={row.value} max={row.max} />
                          ))}
                        </div>
                        <div className="space-y-3">
                          <div className="rounded-md border border-red-200 bg-red-50 p-2.5">
                            <p className="text-[11px] font-semibold text-red-800 mb-1">Key risk indicators</p>
                            <div className="space-y-1">
                              {(topRisks.length > 0 ? topRisks : ['No critical red flags']).map((risk) => (
                                <p key={risk} className="text-[11px] text-red-700 flex items-center gap-1.5">
                                  <AlertTriangle className="w-3 h-3 shrink-0" />
                                  <span>{risk}</span>
                                </p>
                              ))}
                            </div>
                          </div>

                          <div className="rounded-md border border-[#2B2B2B]/10 bg-[#F8F7F4] p-2.5">
                            <p className="text-[11px] font-semibold text-[#2B2B2B]">Ranking context</p>
                            <div className="mt-1 space-y-1 text-[11px] text-[#5a5a5a]">
                              {rankingTopThree.length > 0 ? rankingTopThree.map((entry, idx) => (
                                <p key={entry.id}>
                                  #{idx + 1} - {entry.user?.name || entry.user?.email || 'Applicant'}: {toNumber(entry.application_score).toFixed(1)}
                                  {selectedApp.id === entry.id ? ' (current)' : ''}
                                </p>
                              )) : <p>No ranking data available</p>}
                              {selectedRank >= 0 && <p className="text-[#2B2B2B]">Current rank: #{selectedRank + 1}</p>}
                            </div>
                          </div>

                          <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5">
                            <p className="text-[11px] font-semibold text-amber-800">Decision recommendation: {recommendation.label}</p>
                            <p className="text-[11px] text-amber-700">{recommendation.reason}</p>
                          </div>

                          {scoreUnlocks.length > 0 && (
                            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5" data-testid="score-unlocks">
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-[11px] font-semibold text-emerald-800">
                                  Score unlocks available: up to +{unlockTotal} pts
                                </p>
                                {unlockMailto && (
                                  <a
                                    href={unlockMailto}
                                    className="text-[10px] font-semibold text-emerald-800 underline hover:text-emerald-900"
                                    data-testid="unlock-email-applicant"
                                  >
                                    Email applicant
                                  </a>
                                )}
                              </div>
                              <ul className="space-y-1">
                                {scoreUnlocks.map((item) => (
                                  <li key={item.id} className="text-[11px] text-emerald-800 flex items-start gap-1.5">
                                    <span className={`mt-0.5 inline-flex items-center justify-center rounded px-1 py-0.5 text-[9px] font-bold ${item.soft ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-600 text-white'}`}>
                                      +{item.delta}
                                    </span>
                                    <span className="min-w-0 flex-1">
                                      <span className="font-medium">{item.label}</span>
                                      <span className="block text-[10px] text-emerald-700/80">{item.hint}</span>
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border border-[#2B2B2B]/10 rounded-md p-3" data-testid="home-search-panel">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] uppercase tracking-wider text-[#5a5a5a] font-semibold">Home Search</p>
                        <p className="text-[10px] text-[#5a5a5a]">
                          {(selectedApp.listing_proposals || []).length} listings · {(selectedApp.dossier_pushes || []).length} pushes
                        </p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {/* Listings proposed to applicant */}
                        <div className="space-y-2">
                          <p className="text-[11px] font-semibold text-[#2B2B2B]">Propose a listing to applicant</p>
                          <Select value={proposalAptId} onValueChange={setProposalAptId}>
                            <SelectTrigger className="h-9 text-xs" data-testid="propose-apt-select">
                              <SelectValue placeholder="Select an apartment..." />
                            </SelectTrigger>
                            <SelectContent>
                              {apartments.length === 0 && <SelectItem value="__empty__" disabled className="text-xs">No apartments available</SelectItem>}
                              {apartments.map(apt => {
                                const unavailable = apt.status && apt.status !== 'available';
                                return (
                                  <SelectItem key={apt.id} value={apt.id} disabled={unavailable} className="text-xs">
                                    {apt.title} {'\u2014'} CHF {apt.rent}/mo {'\u2014'} {apt.rooms}rm
                                    {apt.property_manager ? ` \u00b7 ${apt.property_manager.company || apt.property_manager.last_name}` : ''}
                                    {unavailable ? ` (${apt.status})` : ''}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          <Textarea
                            value={proposalMsg}
                            onChange={(e) => setProposalMsg(e.target.value)}
                            placeholder="Optional note shown to the applicant..."
                            className="text-xs min-h-[60px]"
                            maxLength={1000}
                            data-testid="propose-message-input"
                          />
                          <Button
                            size="sm"
                            className="h-8 text-[11px] bg-[#0B3D2E] text-white hover:bg-[#0f523e] disabled:opacity-50 w-full"
                            onClick={handleProposeListing}
                            disabled={savingProposal || !proposalAptId}
                            data-testid="propose-listing-btn"
                          >
                            {savingProposal ? 'Sending...' : 'Propose listing'}
                          </Button>

                          <div className="space-y-1.5 pt-2 border-t border-[#2B2B2B]/5">
                            {(selectedApp.listing_proposals || []).length === 0 && (
                              <p className="text-[11px] text-[#5a5a5a]">No listings proposed yet.</p>
                            )}
                            {(selectedApp.listing_proposals || []).map((p) => {
                              const statusTone = {
                                proposed:  'bg-blue-50 text-blue-700',
                                interested:'bg-emerald-50 text-emerald-700',
                                declined:  'bg-slate-100 text-slate-600',
                                withdrawn: 'bg-gray-100 text-gray-500',
                              }[p.status] || 'bg-gray-100 text-gray-600';
                              const isEditing = editingProposalId === p.id;
                              const aptUrl = p.apartment?.external_url;
                              return (
                                <div key={p.id} className="rounded-md border border-[#2B2B2B]/10 p-2 text-[11px]" data-testid={`proposal-${p.id}`}>
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <p className="text-[#2B2B2B] truncate font-medium">{p.apartment?.title || 'Listing'}</p>
                                      <p className="text-[10px] text-[#5a5a5a]">
                                        CHF {p.apartment?.rent ?? '-'}/mo · {p.apartment?.rooms ?? '-'}rm
                                        {p.apartment?.property_manager ? ` · ${p.apartment.property_manager.company || p.apartment.property_manager.last_name}` : ''}
                                      </p>
                                      {aptUrl && (
                                        <a
                                          href={aptUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-1 text-[10px] text-[#0B3D2E] underline hover:no-underline mt-1"
                                          data-testid={`proposal-link-${p.id}`}
                                        >
                                          <ExternalLink className="w-3 h-3" /> View listing
                                        </a>
                                      )}
                                      {!isEditing && p.message && (
                                        <p className="text-[10px] text-[#2B2B2B]/70 mt-1 line-clamp-2">{p.message}</p>
                                      )}
                                      {p.availability && (
                                        <div className="mt-1.5 rounded bg-emerald-50 border border-emerald-100 p-1.5">
                                          <p className="text-[9px] uppercase tracking-wide text-emerald-700 font-semibold">Tenant availability</p>
                                          <p className="text-[10px] text-[#2B2B2B] whitespace-pre-wrap">{p.availability}</p>
                                        </div>
                                      )}
                                    </div>
                                    <Badge className={`${statusTone} border-0 text-[9px] capitalize shrink-0`}>{p.status}</Badge>
                                  </div>
                                  {isEditing && (
                                    <div className="mt-2 space-y-1.5">
                                      <Textarea
                                        rows={3}
                                        value={editingProposalMsg}
                                        onChange={(e) => setEditingProposalMsg(e.target.value)}
                                        placeholder="Note to tenant about this listing..."
                                        className="text-[11px]"
                                        disabled={savingProposalEdit}
                                        data-testid={`edit-proposal-input-${p.id}`}
                                      />
                                      <div className="flex items-center justify-end gap-1">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-6 text-[10px] px-2"
                                          onClick={handleCancelEditProposal}
                                          disabled={savingProposalEdit}
                                        >
                                          Cancel
                                        </Button>
                                        <Button
                                          size="sm"
                                          className="h-6 text-[10px] px-2 bg-[#0B3D2E] text-white"
                                          onClick={() => handleSaveProposalEdit(p.id)}
                                          disabled={savingProposalEdit}
                                          data-testid={`save-proposal-${p.id}`}
                                        >
                                          <Check className="w-3 h-3 mr-1" /> Save
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                  {!isEditing && (
                                    <div className="flex items-center justify-end gap-1 mt-1.5">
                                      <button
                                        onClick={() => handleStartEditProposal(p)}
                                        className="text-[#5a5a5a] hover:text-[#0B3D2E] inline-flex items-center gap-1 text-[10px]"
                                        aria-label="Edit"
                                        data-testid={`edit-proposal-${p.id}`}
                                      >
                                        <Pencil className="w-3 h-3" /> Edit
                                      </button>
                                      {p.status === 'proposed' && (
                                        <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => handleWithdrawProposal(p.id)} data-testid={`withdraw-${p.id}`}>
                                          Withdraw
                                        </Button>
                                      )}
                                      <button
                                        onClick={() => handleDeleteProposal(p.id)}
                                        className="text-[#5a5a5a] hover:text-red-600"
                                        aria-label="Delete"
                                        data-testid={`delete-proposal-${p.id}`}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Dossier pushes to PMs */}
                        <div className="space-y-2">
                          <p className="text-[11px] font-semibold text-[#2B2B2B]">Push dossier to a property manager</p>
                          <Select value={pushPmId} onValueChange={setPushPmId}>
                            <SelectTrigger className="h-9 text-xs" data-testid="push-pm-select">
                              <SelectValue placeholder="Select a property manager..." />
                            </SelectTrigger>
                            <SelectContent>
                              {propertyManagers.filter(pm => !pm.archived).length === 0 && (
                                <SelectItem value="__empty__" disabled className="text-xs">No property managers yet</SelectItem>
                              )}
                              {propertyManagers.filter(pm => !pm.archived).map(pm => (
                                <SelectItem key={pm.id} value={pm.id} className="text-xs">
                                  {pm.first_name} {pm.last_name}{pm.company ? ` · ${pm.company}` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Textarea
                            value={pushMsg}
                            onChange={(e) => setPushMsg(e.target.value)}
                            placeholder="Optional context for the PM (internal + email body hint)..."
                            className="text-xs min-h-[60px]"
                            maxLength={1000}
                            data-testid="push-message-input"
                          />
                          {(() => {
                            const pm = propertyManagers.find(p => p.id === pushPmId);
                            const applicantName = selectedApp.user?.name || selectedApp.user?.email || 'Applicant';
                            const subject = encodeURIComponent(`Nest Geneva - Dossier for ${applicantName}`);
                            const body = encodeURIComponent(
                              `Hi ${pm ? pm.first_name : ''},\n\n` +
                              `We would like to submit the dossier of ${applicantName} for your consideration.\n\n` +
                              (pushMsg ? `${pushMsg}\n\n` : '') +
                              `Best regards,\nNest Geneva`
                            );
                            const mailto = pm ? `mailto:${pm.email}?subject=${subject}&body=${body}` : null;
                            return (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  className="h-8 text-[11px] bg-[#0B3D2E] text-white hover:bg-[#0f523e] disabled:opacity-50 flex-1"
                                  onClick={handlePushDossier}
                                  disabled={savingPush || !pushPmId}
                                  data-testid="push-dossier-btn"
                                >
                                  {savingPush ? 'Recording...' : 'Record push'}
                                </Button>
                                {mailto && (
                                  <a
                                    href={mailto}
                                    className="inline-flex items-center justify-center h-8 px-3 rounded-md border border-[#0B3D2E] text-[11px] text-[#0B3D2E] hover:bg-[#0B3D2E]/5"
                                    data-testid="push-mailto-btn"
                                  >
                                    Email PM
                                  </a>
                                )}
                              </div>
                            );
                          })()}

                          <div className="space-y-1.5 pt-2 border-t border-[#2B2B2B]/5">
                            {(selectedApp.dossier_pushes || []).length === 0 && (
                              <p className="text-[11px] text-[#5a5a5a]">No pushes yet.</p>
                            )}
                            {(selectedApp.dossier_pushes || []).map((p) => {
                              const statusTone = {
                                sent:        'bg-blue-50 text-blue-700',
                                accepted:    'bg-emerald-50 text-emerald-700',
                                declined:    'bg-red-50 text-red-700',
                                no_response: 'bg-slate-100 text-slate-600',
                              }[p.status] || 'bg-gray-100 text-gray-600';
                              const pm = p.property_manager;
                              return (
                                <div key={p.id} className="rounded-md border border-[#2B2B2B]/10 p-2 text-[11px]" data-testid={`push-${p.id}`}>
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <p className="text-[#2B2B2B] truncate font-medium">
                                        {pm ? `${pm.first_name} ${pm.last_name}` : 'Unknown PM'}
                                        {pm?.company ? ` · ${pm.company}` : ''}
                                      </p>
                                      <p className="text-[10px] text-[#5a5a5a]">
                                        {pm?.email || '-'} · sent {p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}
                                      </p>
                                      {p.message && <p className="text-[10px] text-[#2B2B2B]/70 mt-1 line-clamp-2">{p.message}</p>}
                                    </div>
                                    <Badge className={`${statusTone} border-0 text-[9px] capitalize shrink-0`}>{p.status.replace('_', ' ')}</Badge>
                                  </div>
                                  <div className="flex items-center justify-end gap-1 mt-1.5">
                                    <Select value={p.status} onValueChange={(v) => handleUpdatePushStatus(p.id, v)}>
                                      <SelectTrigger className="h-6 w-[110px] text-[10px]" data-testid={`push-status-${p.id}`}><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="sent" className="text-[10px]">Sent</SelectItem>
                                        <SelectItem value="accepted" className="text-[10px]">Accepted</SelectItem>
                                        <SelectItem value="declined" className="text-[10px]">Declined</SelectItem>
                                        <SelectItem value="no_response" className="text-[10px]">No response</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <button
                                      onClick={() => handleDeletePush(p.id)}
                                      className="text-[#5a5a5a] hover:text-red-600"
                                      aria-label="Delete"
                                      data-testid={`delete-push-${p.id}`}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border border-[#2B2B2B]/10 rounded-md p-3" data-testid="communications-card">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] uppercase tracking-wider text-[#5a5a5a] font-semibold flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {t('admin.email.communications')}
                        </p>
                        <p className="text-[10px] text-[#5a5a5a]">
                          {(selectedApp.email_drafts || []).length}
                        </p>
                      </div>
                      {(selectedApp.email_drafts || []).length === 0 ? (
                        <p className="text-[11px] text-[#5a5a5a]">{t('admin.email.empty')}</p>
                      ) : (
                        <div className="space-y-1.5">
                          {(selectedApp.email_drafts || []).map((d) => {
                            const tone = {
                              sent:    'bg-emerald-50 text-emerald-700',
                              pending: 'bg-amber-50 text-amber-700',
                              skipped: 'bg-slate-100 text-slate-600',
                              failed:  'bg-red-50 text-red-700',
                            }[d.status] || 'bg-gray-100 text-gray-600';
                            const isOpen = previewEmailId === d.id;
                            return (
                              <div key={d.id} className="rounded-md border border-[#2B2B2B]/10 text-[11px]" data-testid={`email-draft-${d.id}`}>
                                <button
                                  type="button"
                                  onClick={() => setPreviewEmailId(isOpen ? '' : d.id)}
                                  className="w-full p-2 text-left hover:bg-[#0B3D2E]/5"
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <p className="text-[#2B2B2B] font-medium truncate">{d.subject || '(no subject)'}</p>
                                      <p className="text-[10px] text-[#5a5a5a] truncate">
                                        {d.event_key} {'\u00b7'} {d.recipient_email || '-'} {'\u00b7'} {d.language?.toUpperCase()}
                                      </p>
                                      <p className="text-[10px] text-[#5a5a5a]">
                                        {d.decided_at ? new Date(d.decided_at).toLocaleString() : d.created_at ? new Date(d.created_at).toLocaleString() : ''}
                                        {d.decided_by_name ? ` ${'\u00b7'} ${d.decided_by_name}` : ''}
                                      </p>
                                    </div>
                                    <Badge className={`${tone} text-[10px]`}>
                                      {t(`admin.email.status.${d.status}`)}
                                    </Badge>
                                  </div>
                                </button>
                                {isOpen && (
                                  <div className="border-t border-[#2B2B2B]/10 p-2 bg-[#fafafa]">
                                    <pre className="whitespace-pre-wrap text-[11px] text-[#2B2B2B] font-sans">
{d.body_text || '(no plain text)'}
                                    </pre>
                                    {d.status === 'failed' && d.error && (
                                      <p className="mt-2 text-[10px] text-red-600" data-testid={`email-draft-error-${d.id}`}>
                                        {d.error}
                                      </p>
                                    )}
                                    {d.status === 'sent' && d.provider_message_id && (
                                      <p className="mt-2 text-[10px] text-[#5a5a5a]">
                                        Provider id: {d.provider_message_id}
                                      </p>
                                    )}
                                    {(d.status === 'pending' || d.status === 'failed' || (d.status === 'skipped' && d.skip_reason === 'dismissed')) && (
                                      <div className="mt-2 flex justify-end">
                                        <Button
                                          size="sm"
                                          className="h-7 text-[11px]"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setEmailDraft(d);
                                            setPreviewEmailId('');
                                          }}
                                          data-testid={`email-draft-retry-${d.id}`}
                                        >
                                          {t('admin.email.retry')}
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="bg-white border border-[#2B2B2B]/10 rounded-md p-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] uppercase tracking-wider text-[#5a5a5a] font-semibold">Documents ({docsCount})</p>
                      </div>
                      <div className="space-y-1.5">
                        {(selectedApp.documents || []).map((doc) => (
                          <div key={doc.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border border-[#2B2B2B]/10 rounded-md px-2 py-1.5 text-xs" data-testid={`dossier-doc-${doc.id}`}>
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="w-3.5 h-3.5 text-[#0B3D2E] shrink-0" />
                              <div className="min-w-0">
                                <p className="text-[#2B2B2B] truncate">{compactDocTypeLabel(doc.doc_type)}</p>
                                <p className="text-[10px] text-[#5a5a5a] truncate">{doc.original_filename || 'document'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Button size="sm" variant="outline" className="h-7 text-[11px] px-2" onClick={() => handlePreviewDocument(doc.id)} data-testid={`preview-doc-${doc.id}`}>
                                <ExternalLink className="w-3 h-3 mr-1" />
                                Preview
                              </Button>
                              <Button size="sm" className="h-7 text-[11px] px-2 bg-[#0B3D2E] text-white hover:bg-[#0f523e]" onClick={() => handleDownloadDocument(doc)} data-testid={`download-doc-${doc.id}`}>
                                <Download className="w-3 h-3 mr-1" />
                                Download
                              </Button>
                            </div>
                          </div>
                        ))}
                        {docsCount === 0 && <p className="text-xs text-[#5a5a5a]">No uploaded documents</p>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Property Manager create/edit dialog */}
      <Dialog open={showPmDialog} onOpenChange={setShowPmDialog}>
        <DialogContent className="max-w-lg w-[calc(100vw-2rem)]" data-testid="pm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingPm ? t('admin.editPropertyManager') : t('admin.addPropertyManager')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">First name *</Label><Input value={pmForm.first_name} onChange={e => setPmForm(p => ({ ...p, first_name: e.target.value }))} className="mt-1" data-testid="pm-first-name-input" /></div>
              <div><Label className="text-xs">Last name *</Label><Input value={pmForm.last_name} onChange={e => setPmForm(p => ({ ...p, last_name: e.target.value }))} className="mt-1" data-testid="pm-last-name-input" /></div>
            </div>
            <div><Label className="text-xs">Email *</Label><Input type="email" value={pmForm.email} onChange={e => setPmForm(p => ({ ...p, email: e.target.value }))} className="mt-1" data-testid="pm-email-input" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Phone</Label><Input value={pmForm.phone} onChange={e => setPmForm(p => ({ ...p, phone: e.target.value }))} className="mt-1" data-testid="pm-phone-input" /></div>
              <div><Label className="text-xs">Company</Label><Input value={pmForm.company} onChange={e => setPmForm(p => ({ ...p, company: e.target.value }))} className="mt-1" data-testid="pm-company-input" /></div>
            </div>
            <div><Label className="text-xs">Internal notes</Label><Textarea value={pmForm.notes} onChange={e => setPmForm(p => ({ ...p, notes: e.target.value }))} className="mt-1 min-h-[60px]" data-testid="pm-notes-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowPmDialog(false)}>Cancel</Button>
            <Button className="bg-[#0B3D2E] text-white" onClick={handleSavePm} disabled={savingPm} data-testid="save-pm-btn">
              {savingPm ? 'Saving...' : (editingPm ? 'Save changes' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EmailApprovalDialog
        draft={emailDraft}
        open={!!emailDraft}
        onClose={async () => {
          const appId = selectedApp?.id;
          setEmailDraft(null);
          if (appId) {
            try {
              const res = await api.get(`/applications/${appId}/emails`);
              setSelectedApp(prev => prev && prev.id === appId ? { ...prev, email_drafts: res.data || [] } : prev);
            } catch {
              // Non-fatal - the list will refresh on next dossier open.
            }
          }
        }}
      />
    </div>
  );
}

function scoreBarColor(score) {
  if (score >= 85) return 'bg-green-600';
  if (score >= 70) return 'bg-amber-500';
  return 'bg-red-500';
}

function compactDocTypeLabel(docType) {
  const normalized = String(docType || '').replace(/_/g, ' ').trim();
  if (!normalized) return 'Document';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function ScoreBar({ label, value, max }) {
  const score = toNumber(value);
  const pct = max > 0 ? Math.max(0, Math.min(100, (score / max) * 100)) : 0;
  const colorClass = scoreBarColor((score / Math.max(max, 1)) * 100);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-[#2B2B2B]">{label}</span>
        <span className="text-[#5a5a5a]">{score.toFixed(1)}</span>
      </div>
      <div className="h-1.5 rounded-full bg-[#2B2B2B]/10 overflow-hidden">
        <div className={`h-full ${colorClass}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
